// AuthContext.tsx - Authentication Context and Provider
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService, { User, LoginRequest, RegisterRequest } from './apiService';

import logger from './utils/logger';
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<any>;
  loginWithOAuth: (user: User) => void;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  // Always start with loading true to prevent race conditions
  // Will be set to false after attempting to load user from storage
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      logger.log('🔄 Auth initialization starting...');
      
      // Debug: Log all localStorage keys
      logger.log('📦 localStorage contents:', {
        keys: Object.keys(localStorage),
        currentUser: localStorage.getItem('currentUser') ? 'exists' : 'null',
        authTimestamp: localStorage.getItem('authTimestamp'),
        userAgent: navigator.userAgent
      });
      
      try {
        const savedUser = localStorage.getItem('currentUser');
        
        logger.log('🔍 Checking stored user:', { 
          hasUser: !!savedUser
        });
        
        if (savedUser) {
          // Check if auth is recent (within last 60 seconds)
          const authTimestamp = localStorage.getItem('authTimestamp');
          const isRecentAuth = authTimestamp && (Date.now() - parseInt(authTimestamp)) < 60000;
          
          if (isRecentAuth) {
            // Skip API verification for recent OAuth logins
            try {
              const cachedUser = JSON.parse(savedUser);
              logger.log('🚀 Using recent OAuth user data (skipping API verification):', cachedUser.email);
              setUser(cachedUser);
            } catch (parseError) {
              logger.error('❌ Failed to parse cached user data:', parseError);
              localStorage.removeItem('currentUser');
              // Tokens are httpOnly cookies handled by backend, no need to remove
            }
          } else {
            // Normal API verification for non-recent logins
            try {
              // Try to verify token is still valid with a timeout
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('API timeout')), 5000)
              );
              
              logger.log('⏳ Verifying token with API...');
              const currentUserPromise = apiService.getCurrentUser();
              const currentUser = await Promise.race([currentUserPromise, timeoutPromise]) as User;
              
              logger.log('✅ API verification successful, user loaded:', currentUser.email);
              setUser(currentUser);
              // Update localStorage with fresh user data
              localStorage.setItem('currentUser', JSON.stringify(currentUser));
            } catch (apiError) {
              logger.warn('⚠️ API verification failed, clearing cached data:', apiError);
              // If API returns 401 or fails, user is not authenticated
              // Clear localStorage and don't set user
              localStorage.removeItem('currentUser');
              localStorage.removeItem('authTimestamp');
              setUser(null);
            }
          }
        } else {
          logger.log('🚫 No stored credentials found');
        }
      } catch (error) {
        logger.error('❌ Auth initialization error:', error);
        // Clear any corrupted auth data
        localStorage.removeItem('currentUser');
      } finally {
        logger.log('🏁 Auth initialization completed, loading finished');
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      logger.log('🔑 Attempting login for:', credentials.email);
      const response = await apiService.login(credentials);
      logger.log('✅ Login successful:', response.user.email);
      
      setUser(response.user);
      setIsLoading(false); // Explicitly set loading to false after successful login
      // Ensure we have the user data cached
      localStorage.setItem('currentUser', JSON.stringify(response.user));
      
      // Check if first-time user needs onboarding
      if (response.user.isFirstLogin) {
        logger.log('🎉 First-time user, returning for onboarding');
        return response; // Return response so caller can handle onboarding
      } else {
        logger.log('🏠 Navigating to home...');
        navigate('/home');
      }
      
      // Preload likely next pages in background for instant navigation
      setTimeout(() => {
        import('./HomePageV2');
        import('./QuizSetupV2');
        import('./StudyModePageV2');
      }, 1000);
      
      return response;
    } catch (error) {
      logger.error('❌ Login error:', error);
      setIsLoading(false); // Also set loading to false on error
      throw error;
    }
  };

  const loginWithOAuth = (oauthUser: User) => {
    logger.log('🔑 OAuth login for:', oauthUser.email);
    setUser(oauthUser);
    setIsLoading(false);
    localStorage.setItem('currentUser', JSON.stringify(oauthUser));
    // Store timestamp to skip API verification on immediate page reload
    localStorage.setItem('authTimestamp', Date.now().toString());
    logger.log('✅ OAuth login successful');
    // Do NOT navigate here - let the calling component handle navigation
    
    // Preload likely next pages in background for instant navigation
    setTimeout(() => {
      import('./HomePageV2');
      import('./QuizSetupV2');
      import('./StudyModePageV2');
    }, 1000);
  };

  const register = async (data: RegisterRequest) => {
    try {
      const { user } = await apiService.register(data);
      setUser(user);
      setIsLoading(false); // Explicitly set loading to false after successful registration
      localStorage.setItem('currentUser', JSON.stringify(user));
      navigate('/home');
    } catch (error) {
      logger.error('Registration error:', error);
      setIsLoading(false); // Also set loading to false on error
      throw error;
    }
  };

  const logout = async () => {
    try {
      logger.log('🚪 Logout initiated');
      
      // Log current state before clearing
      logger.log('📦 Pre-logout localStorage:', {
        currentUser: localStorage.getItem('currentUser') ? 'exists' : 'null',
        authTimestamp: localStorage.getItem('authTimestamp'),
        allKeys: Object.keys(localStorage)
      });
      
      // Clear user state and localStorage immediately
      setUser(null);
      localStorage.removeItem('currentUser');
      localStorage.removeItem('authTimestamp');
      
      // Clear service worker caches to prevent cached auth responses
      if ('caches' in window) {
        // Clear API cache specifically
        caches.delete('valulearn-api-v2.0.2').catch(() => {});
        caches.delete('valulearn-api-v2.0.1').catch(() => {});
        
        // Send message to service worker to clear all auth-related caches
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_AUTH_CACHE'
          });
        }
      }
      
      // Then call API to clear cookies
      await apiService.logout();
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      // Navigate after everything is cleared
      navigate('/');
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    setIsLoading(false); // Ensure loading state is cleared when user is set
  };

  const refreshUser = async () => {
    try {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
    } catch (error) {
      logger.error('Error refreshing user:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    loginWithOAuth,
    register,
    logout,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireRole?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true,
  requireRole 
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    logger.log('🛡️ ProtectedRoute check:', { isLoading, isAuthenticated, hasUser: !!user });
    
    if (!isLoading) {
      if (requireAuth && !isAuthenticated) {
        logger.log('🔄 Redirecting to login - user not authenticated');
        navigate('/login');
      } else if (requireRole && user && !requireRole.includes(user.role)) {
        logger.log('🚫 Redirecting to unauthorized - insufficient role');
        navigate('/unauthorized');
      }
    }
  }, [isAuthenticated, user, isLoading, requireAuth, requireRole, navigate]);

  if (isLoading) {
    logger.log('⏳ ProtectedRoute showing loading spinner', { isLoading, isAuthenticated, hasUser: !!user });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue"></div>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return null;
  }

  if (requireRole && user && !requireRole.includes(user.role)) {
    return null;
  }

  return <>{children}</>;
};

// ProtectedAdminRoute Component
export const ProtectedAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ProtectedRoute requireRole={['admin']}>{children}</ProtectedRoute>;
};